import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

const RecordingsLibrary = () => {
  const history = useHistory();
  const [recordings, setRecordings] = useState([]);
  const [filteredRecordings, setFilteredRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Sample data - replace with actual API call
  const sampleRecordings = [
    {
      id: 'CJd196e069d5b55a554f29932063e29db6',
      appointmentId: '115',
      patientName: 'Shubham kale',
      patientId: '60',
      date: '2023-10-15T14:30:00Z',
      duration: '00:25:43',
      status: 'completed',
      downloadUrl: '/v1/Compositions/CJd196e069d5b55a554f29932063e29db6/Media',
      size: '125MB'
    },
    {
      id: 'CJd196e069d5b55a554f29932063e29db6',
      appointmentId: 'APT-2023-00126',
      patientName: 'Emily Johnson',
      patientId: 'P-12346',
      date: '2023-10-16T10:15:00Z',
      duration: '00:32:15',
      status: 'completed',
      downloadUrl: '/v1/Compositions/CJd196e069d5b55a554f29932063e29db6/Media',
      size: '158MB'
    },
    {
      id: 'CJa7b3c2d1e8f9g0h1i2j3k4l5m6n7o8p9',
      appointmentId: 'APT-2023-00127',
      patientName: 'Robert Williams',
      patientId: 'P-12347',
      date: '2023-10-17T16:45:00Z',
      duration: '00:18:29',
      status: 'processing',
      downloadUrl: null,
      size: '0MB'
    }
  ];

  useEffect(() => {
    // Simulate API fetch
    const fetchRecordings = async () => {
      try {
        // In a real application, you would fetch from your API
        // const response = await fetch('/api/recordings');
        // const data = await response.json();
        
        setRecordings(sampleRecordings);
        setFilteredRecordings(sampleRecordings);
        setLoading(false);
      } catch (err) {
        setError('Failed to load recordings');
        setLoading(false);
      }
    };

    fetchRecordings();
  }, []);

  useEffect(() => {
    let results = recordings;
    
    // Apply search filter
    if (searchTerm) {
      results = results.filter(recording => 
        recording.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recording.appointmentId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      results = results.filter(recording => recording.status === statusFilter);
    }
    
    // Apply date filter (simplified)
    if (dateFilter !== 'all') {
      const today = new Date();
      const filterDate = new Date();
      
      if (dateFilter === 'today') {
        results = results.filter(recording => {
          const recordingDate = new Date(recording.date);
          return recordingDate.toDateString() === today.toDateString();
        });
      } else if (dateFilter === 'week') {
        filterDate.setDate(today.getDate() - 7);
        results = results.filter(recording => new Date(recording.date) >= filterDate);
      } else if (dateFilter === 'month') {
        filterDate.setMonth(today.getMonth() - 1);
        results = results.filter(recording => new Date(recording.date) >= filterDate);
      }
    }
    
    setFilteredRecordings(results);
  }, [searchTerm, statusFilter, dateFilter, recordings]);

  const handlePlayRecording = (recording) => {
    // In a real app, this would open a modal or navigate to a player page
    alert(`Would play recording: ${recording.id}`);
  };

  const handleDownload = (recording) => {
    if (recording.status === 'completed' && recording.downloadUrl) {
      // In a real app, this would initiate download
      alert(`Would download recording: ${recording.id}`);
    } else {
      alert('Recording is not available for download yet.');
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading recordings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <h3>Error Loading Recordings</h3>
        <p>{error}</p>
        <button 
          style={styles.retryButton}
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Video Call Recordings</h1>
        <p style={styles.subtitle}>Review and manage your consultation recordings</p>
      </div>

      <div style={styles.filtersContainer}>
        <div style={styles.searchBox}>
          <input
            type="text"
            placeholder="Search by patient name or appointment ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Status:</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="processing">Processing</option>
          </select>
        </div>
        
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Date:</label>
          <select 
            value={dateFilter} 
            onChange={(e) => setDateFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="week">Past Week</option>
            <option value="month">Past Month</option>
          </select>
        </div>
      </div>

      <div style={styles.resultsInfo}>
        <p>{filteredRecordings.length} recording(s) found</p>
      </div>

      <div style={styles.recordingsList}>
        {filteredRecordings.length === 0 ? (
          <div style={styles.emptyState}>
            <i className="material-icons" style={styles.emptyIcon}>playlist_remove</i>
            <h3>No recordings found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          filteredRecordings.map(recording => (
            <div key={recording.id} style={styles.recordingCard}>
              <div style={styles.recordingInfo}>
                <div style={styles.patientInfo}>
                  <h3 style={styles.patientName}>{recording.patientName}</h3>
                  <p style={styles.patientId}>ID: {recording.patientId}</p>
                </div>
                
                <div style={styles.appointmentInfo}>
                  <p style={styles.appointmentId}>{recording.appointmentId}</p>
                  <p style={styles.recordingDate}>{formatDate(recording.date)}</p>
                </div>
                
                <div style={styles.recordingMeta}>
                  <span style={styles.metaItem}>
                    <i className="material-icons" style={styles.metaIcon}>schedule</i>
                    {recording.duration}
                  </span>
                  <span style={styles.metaItem}>
                    <i className="material-icons" style={styles.metaIcon}>storage</i>
                    {recording.size}
                  </span>
                  <span style={{
                    ...styles.statusBadge,
                    ...(recording.status === 'completed' ? styles.statusCompleted : styles.statusProcessing)
                  }}>
                    {recording.status}
                  </span>
                </div>
              </div>
              
              <div style={styles.actions}>
                <button 
                  style={styles.actionButton}
                  onClick={() => handlePlayRecording(recording)}
                  disabled={recording.status !== 'completed'}
                >
                  <i className="material-icons">play_circle</i>
                  Play
                </button>
                
                <button 
                  style={{
                    ...styles.actionButton,
                    ...styles.downloadButton
                  }}
                  onClick={() => handleDownload(recording)}
                  disabled={recording.status !== 'completed'}
                >
                  <i className="material-icons">cloud_download</i>
                  Download
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh'
  },
  header: {
    marginBottom: '30px',
    paddingBottom: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  title: {
    color: '#2c3e50',
    marginBottom: '5px'
  },
  subtitle: {
    color: '#7f8c8d',
    margin: 0
  },
  filtersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    marginBottom: '20px',
    alignItems: 'center'
  },
  searchBox: {
    flex: '1',
    minWidth: '300px'
  },
  searchInput: {
    width: '100%',
    padding: '10px 15px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px'
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  filterLabel: {
    fontWeight: '500'
  },
  filterSelect: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  resultsInfo: {
    marginBottom: '15px',
    color: '#7f8c8d'
  },
  recordingsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  recordingCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    borderLeft: '4px solid #3498db'
  },
  recordingInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '30px',
    alignItems: 'center'
  },
  patientInfo: {
    minWidth: '200px'
  },
  patientName: {
    margin: '0 0 5px 0',
    color: '#2c3e50'
  },
  patientId: {
    margin: 0,
    color: '#7f8c8d',
    fontSize: '14px'
  },
  appointmentInfo: {
    minWidth: '180px'
  },
  appointmentId: {
    margin: '0 0 5px 0',
    fontWeight: '500'
  },
  recordingDate: {
    margin: 0,
    color: '#7f8c8d',
    fontSize: '14px'
  },
  recordingMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '14px',
    color: '#7f8c8d'
  },
  metaIcon: {
    fontSize: '16px'
  },
  statusBadge: {
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'capitalize',
    width: 'fit-content'
  },
  statusCompleted: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32'
  },
  statusProcessing: {
    backgroundColor: '#fff3e0',
    color: '#ef6c00'
  },
  actions: {
    display: 'flex',
    gap: '10px'
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '8px 15px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s'
  },
  downloadButton: {
    backgroundColor: '#e3f2fd',
    borderColor: '#bbdefb',
    color: '#1565c0'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh'
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #3498db',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
    marginBottom: '15px'
  },
  errorContainer: {
    textAlign: 'center',
    padding: '40px',
    color: '#e74c3c'
  },
  retryButton: {
    padding: '10px 20px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '15px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#7f8c8d'
  },
  emptyIcon: {
    fontSize: '60px',
    marginBottom: '15px',
    color: '#bdc3c7'
  }
};

export default RecordingsLibrary;